/**
 * DexFilterMenu — the Pokédex view controls as a menu.
 *
 * WHAT CHANGED AND WHY
 * The filters used to be a "Filters" pill that popped a single flat panel with
 * every section stacked at once: Search, Region, Status, Type (18 chips), Sort.
 * On a phone that is a wall — you scroll past everything to reach Sort, and the
 * panel tells you nothing about what is currently applied without reading all of
 * it. With an Event section added on top it would only get worse.
 *
 * So it became a MENU: a permanent header row you expand, and inside it one
 * collapsible section per axis, each stating its own current selection so a
 * collapsed menu is still readable.
 *
 * COLLAPSING IS NOW EARNED, NOT AUTOMATIC (this pass). An accordion costs a tap
 * per axis, and that is only worth paying for an axis whose body is big enough
 * to be a wall on its own: Event (a catalogue), Region (12 chips), Type (18
 * chips). The small axes — Sort, Forms, Released/Unreleased — are three or four
 * short chips each, i.e. they FIT on one line beside their own heading. Hiding
 * them behind a header that is itself a whole row traded a row of chrome for a
 * row of chrome AND charged a tap for it. Those come in as `rows`: always
 * rendered, heading on the left, chips on the right, no toggle.
 *
 * (Search left this component entirely for the same reason — see the Dex page:
 * a search box is the one control you should never have to open anything to
 * reach, so it now sits above the menu, always typeable.)
 *
 * Both sections and rows are passed in by the Dex page, which owns the filter
 * state; this component owns only the open/closed mechanics and the chrome.
 */
import { useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronDown, SlidersHorizontal, X } from "lucide-react";

export interface DexFilterSectionDef {
  key: string;
  label: string;
  /** The current selection, shown in the section header when collapsed. */
  summary: string;
  /** True when this section is narrowing anything — drives the accent dot. */
  active: boolean;
  render: () => ReactNode;
}

/**
 * An always-visible axis: heading on the left, its controls on the right, one
 * line, no toggle. For axes whose whole body is a few short chips.
 */
export interface DexFilterRowDef {
  key: string;
  label: string;
  /** True when this row is narrowing anything — drives the accent dot. */
  active: boolean;
  render: () => ReactNode;
}

export function DexFilterMenu({
  sections,
  rows = [],
  anyActive,
  activeCount,
  count,
}: {
  sections: DexFilterSectionDef[];
  /** Small axes rendered open beneath the collapsible sections. */
  rows?: DexFilterRowDef[];
  anyActive: boolean;
  /** How many axes are currently narrowing, for the header summary. */
  activeCount: number;
  /**
   * The page's "x / y" progress figure for the current selection, drawn at
   * the header's right edge beside the chevron. The header is the one line
   * of this menu that is always on screen, which is what a progress figure
   * needs; it moved here from the Dex page's status row when the Evo toggle
   * took that slot (2026-10-19). Null hides it.
   */
  count?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Accordion: at most one section expanded. Opening the menu opens nothing —
  // the summaries are usually all you need, and auto-expanding a section would
  // reintroduce the wall this replaced.
  const [openSection, setOpenSection] = useState<string | null>(null);

  return (
    <div data-testid="dex-filter-menu">
      {/* Menu header — always visible, always states what is applied. */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          data-testid="dex-filter-menu-toggle"
          className="flex-1 min-w-0 text-left rounded-xl px-3 py-1.5 inline-flex items-center gap-2"
          style={{
            // 34px, down from 40 (2026-09-01 compaction pass). This bar, the
            // search box, the status row and four chip rows all stack above the
            // grid, so every row's height is paid four or five times over
            // before a single Pokémon is on screen.
            minHeight: 34,
            background: anyActive ? "var(--accent-fill-glow-faint)" : "var(--surface-dark-9)",
            border: `1px solid ${anyActive ? "var(--accent-fill-glow)" : "var(--accent-fill-glow-subtle)"}`,
            color: anyActive ? "var(--text-ink-bright)" : "var(--text-ink-faint)",
          }}
        >
          <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" aria-hidden />
          <span className="text-[12px] font-bold">Filters</span>
          <span
            className="text-[11px] truncate"
            style={{ color: "var(--text-ink-faint)" }}
            data-testid="dex-filter-menu-summary"
          >
            {anyActive ? `${activeCount} applied` : "None applied"}
          </span>
          {count != null && <span className="ml-auto shrink-0">{count}</span>}
          <ChevronDown
            className={`w-4 h-4 ${count != null ? "" : "ml-auto "}shrink-0 transition-transform`}
            style={{ transform: open ? "rotate(180deg)" : "none" }}
            aria-hidden
          />
        </button>
        {/* No Clear here. The page's own toolbar row above this menu already
            carries one, and both called the same resetFilters — so an active
            filter set put two identical X-Clear buttons on screen a few pixels
            apart. The page-level one wins: it sits with the rest of the dex's
            controls and is visible whether or not this menu is open. */}
      </div>

      {open && (
        <div
          className="mt-1.5 rounded-xl overflow-hidden"
          data-testid="dex-filter-menu-panel"
          style={{
            background: "var(--surface-dark-9)",
            border: "1px solid var(--accent-fill-glow-subtle)",
          }}
        >
          {sections.map((section, i) => {
            const expanded = openSection === section.key;
            return (
              <div
                key={section.key}
                data-testid={`dex-filter-section-${section.key}`}
                style={{
                  borderTop: i === 0 ? "none" : "1px solid var(--accent-fill-glow-subtle)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenSection(expanded ? null : section.key)}
                  aria-expanded={expanded}
                  data-testid={`dex-filter-section-toggle-${section.key}`}
                  className="w-full text-left px-3 py-1 flex items-center gap-2"
                  style={{ minHeight: 30 }}
                >
                  {/* An accent dot beside a section that is narrowing something,
                      so a glance down the collapsed menu shows where the filters
                      are without reading every summary. */}
                  <ActiveDot active={section.active} />
                  <span
                    className="text-[11px] font-bold uppercase tracking-[0.14em] shrink-0"
                    style={{ color: section.active ? "var(--text-ink-bright)" : "var(--text-ink-faint)" }}
                  >
                    {section.label}
                  </span>
                  <span
                    className="text-[11px] truncate ml-auto text-right"
                    style={{ color: "var(--text-tertiary)" }}
                    data-testid={`dex-filter-section-summary-${section.key}`}
                  >
                    {section.summary}
                  </span>
                  <ChevronDown
                    className="w-3.5 h-3.5 shrink-0 transition-transform"
                    style={{
                      color: "var(--text-tertiary)",
                      transform: expanded ? "rotate(180deg)" : "none",
                    }}
                    aria-hidden
                  />
                </button>
                {expanded && (
                  <div
                    className="px-3 pb-2"
                    data-testid={`dex-filter-section-body-${section.key}`}
                  >
                    {section.render()}
                  </div>
                )}
              </div>
            );
          })}

          {/* The open rows. Same dot + heading language as a section header, so
              the panel reads as one list whether an axis collapses or not. */}
          {rows.map((row, i) => (
            <div
              key={row.key}
              data-testid={`dex-filter-row-${row.key}`}
              className="px-3 py-1 flex items-center gap-1.5 flex-wrap"
              style={{
                minHeight: 30,
                borderTop:
                  sections.length === 0 && i === 0
                    ? "none"
                    : "1px solid var(--accent-fill-glow-subtle)",
              }}
            >
              <ActiveDot active={row.active} />
              <span
                className="text-[11px] font-bold uppercase tracking-[0.14em] shrink-0"
                style={{
                  color: row.active ? "var(--text-ink-bright)" : "var(--text-ink-faint)",
                  // A fixed heading column so the chip rows line up with each
                  // other down the panel instead of each starting at its own
                  // word's width. It is a min, not a cap: a longer heading
                  // pushes its own chips right rather than being clipped.
                  minWidth: 54,
                }}
              >
                {row.label}
              </span>
              <div
                className="flex-1 flex flex-wrap gap-1.5 justify-end"
                data-testid={`dex-filter-row-body-${row.key}`}
              >
                {row.render()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The accent dot marking an axis that is currently narrowing the grid. */
function ActiveDot({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden
      className="w-1.5 h-1.5 rounded-full shrink-0"
      style={{
        background: active ? "var(--accent-fill)" : "transparent",
        boxShadow: active ? "0 0 6px var(--accent-fill)" : "none",
      }}
    />
  );
}

/** Shared chip used by every filter section, so all the axes read the same. */
export function FilterChip({
  label,
  active,
  onClick,
  testId,
  accent,
  icon,
  compact,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
  /** Optional per-chip colour (types, event categories); defaults to the app accent. */
  accent?: string;
  icon?: ReactNode;
  /** Fill a grid cell: full width, centred, 9px, tighter padding (Region 6 x 2, 2026-09-29). */
  compact?: boolean;
}) {
  const color = accent ?? "var(--accent-fill-glow)";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={
        compact
          ? "text-[9px] font-bold rounded-full px-1 py-0.5 inline-flex items-center justify-center gap-1 w-full min-w-0 whitespace-nowrap"
          : "text-[10px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1"
      }
      style={{
        // 24px, down from 28 (2026-09-29 owner ask: "tighter together, see
        // more of this menu at once"; 32 before the 2026-09-01 compaction).
        // Still a thumb target inside a panel opened on purpose.
        minHeight: 24,
        // A SELECTED CHIP HAS TO LOOK SELECTED. At 20% fill with a 1px border
        // the default "Any" read as unselected on the dark theme, and the
        // Raids boss filter was reported as having nothing pre-selected when
        // aria-pressed was true the whole time. Twice the fill and a heavier
        // ring is the difference between correct and legible.
        background: active
          ? accent
            ? `color-mix(in srgb, ${accent} 38%, transparent)`
            : "var(--accent-fill-glow)"
          : "var(--surface-dark-9)",
        border: `${active ? 2 : 1}px solid ${active ? color : "var(--accent-fill-glow-subtle)"}`,
        color: active ? "var(--text-ink-bright)" : "var(--text-ink-faint)",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * SORT, as one control instead of four chips.
 *
 * Sort is a 2x2 — key (Dex #, Name) crossed with direction — and the old row
 * drew all four cells as mutually exclusive chips. That is the widest possible
 * way to render a cross, and it hid the direction: you had to work out which
 * way you were sorted from which of the four chips happened to be lit.
 *
 * Here the two axes are separate again. The segments pick the KEY; the active
 * segment carries the direction arrow and flips it when tapped, so the
 * direction is stated rather than inferred and the whole control is about half
 * the width. Switching key starts the new key ascending, which is the reading
 * order people expect on arrival (#1 first, A first).
 *
 * The arrow glyphs keep the old row's language: DOWN is ascending (values grow
 * as you read down the grid), up is descending.
 */
export function SortToggle({
  options,
  activeKey,
  ascending,
  onSelect,
  testIdPrefix,
}: {
  options: { key: string; label: string }[];
  activeKey: string;
  /** Direction of the ACTIVE key. */
  ascending: boolean;
  onSelect: (key: string, ascending: boolean) => void;
  testIdPrefix: string;
}) {
  return (
    <div
      className="inline-flex rounded-full"
      data-testid={`${testIdPrefix}-toggle`}
      style={{
        background: "var(--surface-dark-9)",
        border: "1px solid var(--accent-fill-glow-subtle)",
        padding: 2,
      }}
    >
      {options.map((o) => {
        const active = o.key === activeKey;
        const Arrow = ascending ? ArrowDown : ArrowUp;
        return (
          <button
            key={o.key}
            type="button"
            data-testid={`${testIdPrefix}-${o.key}`}
            aria-pressed={active}
            aria-label={
              active
                ? `Sorted by ${o.label}, ${ascending ? "ascending" : "descending"}. Tap to reverse.`
                : `Sort by ${o.label}`
            }
            // Tapping the ACTIVE segment reverses it; tapping the other one
            // switches key and starts ascending.
            onClick={() => onSelect(o.key, active ? !ascending : true)}
            className="text-[10px] font-bold rounded-full px-2 py-1 inline-flex items-center gap-1"
            style={{
              minHeight: 24,
              background: active ? "var(--accent-fill-glow)" : "transparent",
              border: `1px solid ${active ? "var(--accent-fill-glow)" : "transparent"}`,
              color: active ? "var(--text-ink-bright)" : "var(--text-ink-faint)",
            }}
          >
            {o.label}
            {active && <Arrow className="w-3 h-3" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}
